// contexts/OwnerUnitContext.jsx
// Global context for managing owner's selected unit across all pages

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useOwnerAuth } from '../hooks/auth/useOwnerAuth';

const SELECTED_UNIT_KEY = 'owner-selected-unit';

const OwnerUnitContext = createContext(null);

export const OwnerUnitProvider = ({ children }) => {
  const { owner } = useOwnerAuth();
  const [selectedUnitId, setSelectedUnitId] = useState(() => {
    return localStorage.getItem(SELECTED_UNIT_KEY) || '';
  });

  // Get owner's units with full data - memoized to prevent dependency changes
  const units = useMemo(() => owner?.unitIds || [], [owner?.unitIds]);

  // Persist selected unit to localStorage
  useEffect(() => {
    if (selectedUnitId) {
      localStorage.setItem(SELECTED_UNIT_KEY, selectedUnitId);
    } else {
      localStorage.removeItem(SELECTED_UNIT_KEY);
    }
  }, [selectedUnitId]);

  // Validate that saved unitId still exists in owner's units
  // And auto-select first unit if none selected
  useEffect(() => {
    if (units.length > 0) {
      if (selectedUnitId) {
        // Check if selected unit still exists
        const unitExists = units.some(unit => unit._id === selectedUnitId);
        if (!unitExists) {
          // Select first unit if current selection is invalid
          setSelectedUnitId(units[0]._id);
        }
      } else {
        // Auto-select first unit if none selected
        setSelectedUnitId(units[0]._id);
      }
    }
  }, [units, selectedUnitId]);

  // Get the currently selected unit object
  const selectedUnit = units.find(unit => unit._id === selectedUnitId) || null;

  // Check if owner has multiple units (for showing selector)
  const hasMultipleUnits = units.length > 1;

  // Select a unit by ID
  const selectUnit = useCallback((unitId) => {
    if (unitId && units.some(unit => unit._id === unitId)) {
      setSelectedUnitId(unitId);
    }
  }, [units]);

  // Clear selection (show all units)
  const clearSelection = useCallback(() => {
    setSelectedUnitId('');
  }, []);

  const value = {
    // State
    selectedUnitId,
    selectedUnit,
    units,
    hasMultipleUnits,

    // Actions
    selectUnit,
    clearSelection,
  };

  return (
    <OwnerUnitContext.Provider value={value}>
      {children}
    </OwnerUnitContext.Provider>
  );
};

export const useOwnerUnit = () => {
  const context = useContext(OwnerUnitContext);
  if (!context) {
    throw new Error('useOwnerUnit must be used within an OwnerUnitProvider');
  }
  return context;
};

export default OwnerUnitContext;
